// DevHub: Inline, Google-style hero search — type and see results directly below
// the input (no popup). Queries the Backstage search API via searchApiRef, the same
// underlying API the SearchModal popup uses.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi, useAnalytics } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { searchApiRef } from '@backstage/plugin-search-react';
import type { SearchResult } from '@backstage/plugin-search-common';
import { searchMessages } from '../../i18n/search';
import { rankResults } from '../search/rankResults';
import { useSearchStatus } from '../search/useSearchStatus';
import { SearchResultGroups, buildGroups, visualOrder, limitFindings } from '../search/SearchResultGroups';
import { noBrowserAutofill } from '../noBrowserAutofill';
import './HomeSearch.css';

// Limit the dropdown by FINDING (entity), not by flat row — so several tools show, each with a few of
// its doc pages plus its catalog item, instead of one tool's pages filling the whole list.
const MAX_FINDINGS = 6;
const MAX_DOCS_PER_FINDING = 3;
const DEBOUNCE_MS = 250;

export const HomeSearch = () => {
  const navigate = useNavigate();
  const searchApi = useApi(searchApiRef);
  const analytics = useAnalytics();
  const { t } = useTranslationRef(searchMessages);

  const [term, setTerm] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  // Query lifecycle for the dropdown: 'idle' (no term), 'pending' (debounce +
  // in-flight fetch — show "Searching…"), 'done' (settled — show results or
  // no-results), 'error' (fetch failed — show the unavailable row).
  const [status, setStatus] = useState<'idle' | 'pending' | 'done' | 'error'>('idle');
  // True when a settled zero-result is (likely) because the index is still building, not a real miss.
  const [warming, setWarming] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const checkWarming = useSearchStatus();

  // Flat index order as rendered (doc grouping reorders vs. rank order, so arrow-key nav must follow
  // the VISUAL order, not the raw results array). Uses the same grouping the shared list renders.
  const navOrder = useMemo(() => visualOrder(buildGroups(results)), [results]);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Guards against out-of-order async responses overwriting newer results.
  const requestSeq = useRef(0);

  // Debounced query against the Backstage search API.
  useEffect(() => {
    const trimmed = term.trim();
    if (!trimmed) {
      setResults([]);
      setStatus('idle');
      setActiveIndex(-1);
      return undefined;
    }

    const seq = ++requestSeq.current;
    // Mark pending immediately (covers both the debounce wait and the in-flight
    // fetch) and drop any stale results so the dropdown shows "Searching…" rather
    // than a previous query's hits or a premature "no results".
    setStatus('pending');
    setResults([]);
    setWarming(false);
    setActiveIndex(-1);
    const timer = setTimeout(() => {
      searchApi
        .query({ term: trimmed })
        .then(async res => {
          // Ignore stale responses.
          if (seq !== requestSeq.current) return;
          // Limit by FINDING, not flat row: group first, then keep the top findings (each capped to a
          // few doc rows + its catalog item). A single tool with many doc pages no longer fills the
          // window and hides every other matching tool.
          const ranked = limitFindings(rankResults(res.results, trimmed), {
            maxFindings: MAX_FINDINGS,
            maxDocsPerFinding: MAX_DOCS_PER_FINDING,
          });
          setResults(ranked);
          setStatus('done');
          setActiveIndex(-1);
          // Log every settled search (term + result count) so the hero feeds the same analytics as the modal.
          analytics.captureEvent('search', trimmed, { attributes: { count: ranked.length } });
          // Zero results might just mean the index is still building (cold start) — check and
          // show a "warming up" hint instead of a misleading "no results".
          if (ranked.length === 0) {
            const isWarming = await checkWarming();
            if (seq === requestSeq.current) setWarming(isWarming);
          }
        })
        .catch(() => {
          if (seq !== requestSeq.current) return;
          setResults([]);
          setStatus('error');
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [term, searchApi, analytics, checkWarming]);

  // Close the dropdown on outside click.
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const goTo = useCallback(
    (location: string) => {
      setOpen(false);
      if (/^https?:\/\//.test(location)) {
        window.location.href = location;
      } else {
        navigate(location);
      }
    },
    [navigate],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        if (term) {
          setTerm('');
          setResults([]);
          setStatus('idle');
        } else {
          setOpen(false);
          (e.target as HTMLInputElement).blur();
        }
        return;
      }
      if (!navOrder.length) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
        // Step through the rendered (grouped) order, not the raw rank order.
        setActiveIndex(i => {
          const pos = navOrder.indexOf(i);
          return navOrder[(pos + 1) % navOrder.length];
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex(i => {
          const pos = navOrder.indexOf(i);
          return navOrder[(pos - 1 + navOrder.length) % navOrder.length];
        });
      } else if (e.key === 'Enter') {
        const idx = activeIndex >= 0 ? activeIndex : navOrder[0];
        const target = results[idx];
        if (target) {
          e.preventDefault();
          goTo(target.document.location);
        }
      }
    },
    [results, navOrder, activeIndex, term, goTo],
  );

  const showDropdown = open && term.trim().length > 0;

  return (
    <div className="devhub-home-search" ref={containerRef}>
      {/* Clicking anywhere in the padded bar (icon, gap, the vertical padding above
          and below the input) focuses the input — the visible box is taller than the
          input itself, so without this a click near the edges appears to do nothing.
          This is a MOUSE convenience over an already-focusable <input>; keyboard users
          tab straight to the input, so no keyboard handler / interactive role is needed. */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div
        className="devhub-home-search-hero__bar"
        onClick={() => inputRef.current?.focus()}
      >
        <odx-icon name="core::search" size="md" />
        <input
          ref={inputRef}
          className="devhub-home-search-hero__input"
          type="text"
          role="combobox"
          placeholder={t('placeholder')}
          value={term}
          onChange={e => {
            setTerm(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          aria-label={t('ariaLabel')}
          aria-autocomplete="list"
          aria-expanded={showDropdown}
          aria-controls="devhub-home-search-listbox"
          {...noBrowserAutofill}
        />
      </div>

      {showDropdown && (
        <div
          id="devhub-home-search-listbox"
          className="devhub-home-search__panel"
          role="listbox"
        >
          {status === 'pending' && (
            <div className="devhub-home-search__status" aria-live="polite">
              <span className="devhub-home-search__spinner" aria-hidden="true" />
              {t('searching')}
            </div>
          )}
          {status === 'error' && (
            <div className="devhub-home-search__empty" role="alert">
              {t('unavailable')}
            </div>
          )}
          {status === 'done' && results.length === 0 && warming && (
            <div className="devhub-home-search__empty" role="status">
              {t('warming')}
            </div>
          )}
          {status === 'done' && results.length === 0 && !warming && (
            <div className="devhub-home-search__empty">
              {t('noResultsFor', { term: term.trim() })}
            </div>
          )}
          {status === 'done' && results.length > 0 && (
            <SearchResultGroups
              results={results}
              activeIndex={activeIndex}
              onHover={setActiveIndex}
              onSelect={r => goTo(r.document.location)}
            />
          )}
        </div>
      )}
    </div>
  );
};
